import React, { useState, useEffect } from 'react'
import { KTCard, KTCardBody } from '../../../../_metronic/helpers'
import { KTIcon } from '../../../../_metronic/helpers'

// Local Subcategory type for edit modal
interface SubcategoryItem {
  id: number
  subcategory_name: string
  category_id: number
  description: string
  created_at: string
  updated_at: string
  status: number
}

interface CategoryItem {
  id: number
  category_name: string
}

interface EditSubcategoryProps {
  subcategory: SubcategoryItem
  categories: CategoryItem[]
  onClose: () => void
  onSave: (subcategory: SubcategoryItem) => void
}

const EditSubcategory: React.FC<EditSubcategoryProps> = ({ subcategory, categories, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    subcategory_name: subcategory.subcategory_name,
    category_id: subcategory.category_id,
    description: subcategory.description,
    status: subcategory.status
  })
  const [errors, setErrors] = useState<Record<string, string>>({})

  useEffect(() => {
    setFormData({
      subcategory_name: subcategory.subcategory_name,
      category_id: subcategory.category_id,
      description: subcategory.description,
      status: subcategory.status
    })
  }, [subcategory])

  const validateForm = () => {
    const newErrors: Record<string, string> = {}

    if (!formData.subcategory_name.trim()) {
      newErrors.subcategory_name = 'Subcategory name is required'
    }

    if (!formData.category_id) {
      newErrors.category_id = 'Category is required'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    
    if (validateForm()) {
      onSave({
        ...subcategory,
        subcategory_name: formData.subcategory_name.trim(),
        category_id: formData.category_id,
        description: formData.description.trim(),
        status: formData.status
      })
    }
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: name === 'category_id' ? Number(value) : value
    }))
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }))
    }
  }

  return (
    <div className='modal fade show d-block' style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
      <div className='modal-dialog modal-lg'>
        <div className='modal-content'>
          <KTCard>
            <div className='card-header'>
              <div className='card-title'>
                <h3 className='fw-bold text-dark'>Edit Subcategory</h3>
              </div>
              <div className='card-toolbar'>
                <button
                  type='button'
                  className='btn btn-sm btn-icon btn-active-color-primary'
                  onClick={onClose}
                >
                  <KTIcon iconName='cross' className='fs-1' />
                </button>
              </div>
            </div>
            <KTCardBody>
              <form onSubmit={handleSubmit}>
                <div className='row mb-6'>
                  <div className='col-lg-6'>
                    <label className='col-form-label fw-bold fs-6'>
                      <span className='text-danger'>*</span>Subcategory Name
                    </label>
                    <input
                      type='text'
                      name='subcategory_name'
                      className={`form-control form-control-solid ${errors.subcategory_name ? 'is-invalid' : ''}`}
                      placeholder='Enter subcategory name'
                      value={formData.subcategory_name}
                      onChange={handleChange}
                    />
                    {errors.subcategory_name && <div className='invalid-feedback'>{errors.subcategory_name}</div>}
                  </div>
                  <div className='col-lg-6'>
                    <label className='col-form-label fw-bold fs-6'>Status</label>
                    <select
                      name='status'
                      className='form-select form-select-solid'
                      value={formData.status}
                      onChange={handleChange}
                    >
                      <option value={1}>Active</option>
                      <option value={0}>Inactive</option>
                    </select>
                  </div>
                </div>

                <div className='row mb-6'>
                  <div className='col-lg-12'>
                    <label className='col-form-label fw-bold fs-6'>
                      <span className='text-danger'>*</span>Category
                    </label>
                    <select
                      name='category_id'
                      className={`form-select form-select-solid ${errors.category_id ? 'is-invalid' : ''}`}
                      value={formData.category_id}
                      onChange={handleChange}
                    >
                      <option value=''>Select Category</option>
                      {categories.map(cat => (
                        <option key={cat.id} value={cat.id}>{cat.category_name}</option>
                      ))}
                    </select>
                    {errors.category_id && <div className='invalid-feedback'>{errors.category_id}</div>}
                  </div>
                </div>

                <div className='row mb-6'>
                  <div className='col-lg-12'>
                    <label className='col-form-label fw-bold fs-6'>
                      Description
                    </label>
                    <textarea
                      name='description'
                      className={`form-control form-control-solid ${errors.description ? 'is-invalid' : ''}`}
                      rows={3}
                      placeholder='Enter subcategory description'
                      value={formData.description}
                      onChange={handleChange}
                    />
                    {errors.description && <div className='invalid-feedback'>{errors.description}</div>}
                  </div>
                </div>


                <div className='d-flex justify-content-end'>
                  <button
                    type='button'
                    className='btn btn-light me-3'
                    onClick={onClose}
                  >
                    Cancel
                  </button>
                  <button
                    type='submit'
                    className='btn btn-primary'
                  >
                    <KTIcon iconName='check' className='fs-2' />
                    Update Subcategory
                  </button>
                </div>
              </form>
            </KTCardBody>
          </KTCard>
        </div>
      </div>
    </div>
  )
}

export { EditSubcategory }

